// useEventSource.ts
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useChatSlice } from '../slice';
import { EVENT_SOURCE_RESPONSE_TYPES, SENDER_TYPES } from '../slice/constants';
import { selectCurrentSessionId } from '../slice/selectors';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { selectCurrentProduct, selectSessionUserId } from '../../../layouts/MainLayout/slice/selectors';
import { getAuthToken, RequestService } from '../../../helpers/request';
interface IEventSourceResponse {
  type: typeof EVENT_SOURCE_RESPONSE_TYPES.STRING | typeof EVENT_SOURCE_RESPONSE_TYPES.OBJECT;
  data: string;
  done: boolean;
}

export function useEventSource() {
  const [data, setData] = useState('');
  const [open, setOpen] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const endedRef = useRef(false); // NEW: track clean end
  const MAX_RETRIES = 3;
  const dispatch = useDispatch();
  const sessionUserId = useSelector(selectSessionUserId);
  const { actions } = useChatSlice();
  const currentSessionId = useSelector(selectCurrentSessionId);
  const currentProduct = useSelector(selectCurrentProduct);
  const close = () => {
    endedRef.current = true;
    esRef.current?.close();
    setOpen(false);
  };

  const sendUserQuerry = (message: string, attempt = 0) => {
    const token = getAuthToken();
    const payload = { message, sessionId: currentSessionId || undefined, userId: sessionUserId || undefined, productId: currentProduct };
    const json = JSON.stringify(payload);
    const msg = btoa(unescape(encodeURIComponent(json)));

    const url = `${process.env.REACT_APP_PUBLIC_BACKEND_API_URL}/chat/sse?message=${encodeURIComponent(msg)}`;
    setData('');
    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    esRef.current = es;
    endedRef.current = false;

    es.onopen = () => setOpen(true);

    es.onmessage = (evt) => {
      try {
        const response = JSON.parse(evt.data);
        const eventSourceResponse: IEventSourceResponse = JSON.parse(response.data);
        if (eventSourceResponse.done && eventSourceResponse.type === EVENT_SOURCE_RESPONSE_TYPES.OBJECT) {
          const timelineItem: any = eventSourceResponse.data;

          dispatch(
            actions.setMessage({
              sender: SENDER_TYPES.ASSISTANT,
              message: timelineItem.aiAnswer,
              createdAt: timelineItem.createdAt,
              sessionId: timelineItem.sessionId,
            }),
          );
          endedRef.current = true;
          setOpen(false);
          es.close();
          setData('');
          return;
        } else if (eventSourceResponse.type === EVENT_SOURCE_RESPONSE_TYPES.STRING) {
          setData((prev) => prev + eventSourceResponse.data);
        } else if (eventSourceResponse.type === EVENT_SOURCE_RESPONSE_TYPES.ERROR) {
          dispatch(
            actions.setMessage({
              sender: SENDER_TYPES.ASSISTANT,
              message: eventSourceResponse.data,
              createdAt: new Date().toISOString(),
            }),
          );
          setOpen(false);
          es.close();
          setData('');
          return;
        }
      } catch (error) {
        setOpen(false);
        es.close();
        return;
      }
    };

    es.onerror = async (error) => {
      console.log(error, (error as any).status);
      if ((error as any).status === 401) {
        const result: any = await RequestService.instance.getNewAccessToken();
        const newToken = result.data;
        if (!newToken?.success || !newToken?.data?.access_token) {
          setOpen(false);
          setData((prev) => prev + "\n\nSorry, I'm having trouble processing your request. Please try again.");
          es.close();
          return;
        }

        localStorage.setItem(`token-${process.env.REACT_APP_PUBLIC_URL}`, newToken.data.access_token);
        localStorage.setItem(`refresh-token-${process.env.REACT_APP_PUBLIC_URL}`, newToken.data.refresh_token);
        // Retry with backoff after refreshing token
        es.close();
        if (!endedRef.current && attempt < MAX_RETRIES) {
          const delayMs = Math.min(1000 * Math.pow(2, attempt), 8000);
          setTimeout(() => {
            sendUserQuerry(message, attempt + 1);
          }, delayMs);
        } else {
          setOpen(false);
        }
        return;
      }
      // Non-401 errors: close the stream gracefully
      setOpen(false);
      es.close();
    };

    // optional: return a cleanup fn if caller wants it
    return () => {
      endedRef.current = true;
      es.close();
    };
  };

  const cleanData = () => setData('');

  return { data, open, close, sendUserQuerry, cleanData };
}
